import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import React, { ReactNode } from "react";
import Cookies from "js-cookie";

const tokenName = "ebc_accessToken";
const refreshTokenName = "ebc_refreshToken";
const demoToken = "1";
const expiryPeriod = 7;

const demoEmail = "baker-admin@careerhackers.io";
const demoPassword = "A7bC3dE9fG2";

interface AuthProps {
  children: ReactNode;
  disablePreAuth?: boolean;
}

interface AuthContextType {
  user: any | null;
  authenticating: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  isSuperAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  accessToken: string | undefined;
  refreshToken: string | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProps> = ({
  children,
  disablePreAuth = false,
}) => {
  const router = useRouter();

  const [user, setUser] = useState<any | null>(null);
  const [authenticating, setAuthenticating] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const isLogging = useRef(false);

  const saveAccessToken = (token: string) => {
    Cookies.set(tokenName, token, { expires: expiryPeriod });
  };

  const saveRefreshToken = (token: string) => {
    Cookies.set(refreshTokenName, token, { expires: expiryPeriod });
  };

  const getAccessToken = () => {
    return Cookies.get(tokenName);
  };

  const getRefreshToken = () => {
    return Cookies.get(refreshTokenName);
  };

  const removeAccessToken = () => {
    Cookies.remove(tokenName);
  };

  const removeRefreshToken = () => {
    Cookies.remove(refreshTokenName);
  };

  const authenticateUser = async (email: string, password: string) => {
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });
      const { token } = await response.json();
      const { accessToken, refreshToken } = token;

      saveAccessToken(accessToken);
      saveRefreshToken(refreshToken);

      setIsAuthenticated(true);
      setIsAuthorized(true);
    } catch (error: any) {
      if (error.message === "InvalidException") {
        throw new Error("InvalidException");
      } else {
        throw new Error("UnknownException");
      }
    }
  };

  const authenticateToken = async (userToken: string | undefined) => {
    console.log("Authenticating user...");
    try {
      if (!userToken || userToken !== demoToken) {
        throw new Error("Invalid token");
      }

      console.log("User authenticated");

      setIsAuthenticated(true);
      setIsAuthorized(true);

      setAuthenticating(false);
      return true;
    } catch (error: any) {
      return false;
    }
  };

  const fetchUser = async () => {
    if (isLogging.current) return;
    isLogging.current = true;
    try {
      const accessToken = getAccessToken();
      const response = await fetch("/api/auth/me", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const user = await response.json();
      setUser(user);
      setIsAuthenticated(true);
      setIsAuthorized(true);

      setAuthenticating(false);
      isLogging.current = false;
    } catch (error: any) {
      setAuthenticating(false);
      router.push("/");
    }
  };

  useEffect(() => {
    if (!disablePreAuth) fetchUser();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await authenticateUser(email, password);
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const logout = async () => {
    try {
      removeAccessToken();
      setIsAuthenticated(false);
      setIsAuthorized(false);
      setIsSuperAdmin(false);

      router.push("/");      
    } catch (error: any) {
      alert(error);
    }
  };

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  return (
    <AuthContext.Provider
      value={{
        user,
        authenticating,
        error,
        isAuthenticated,
        isAuthorized,
        isSuperAdmin,
        login,
        logout,
        accessToken,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
